export interface OpenLibraryResult {
  title: string;
  author: string | undefined;
  firstPublishYear: string | undefined;
  isbn: string | undefined;
  coverUrl: string | undefined;
  series: string | undefined;
  seriesNumber: string | undefined;
}

interface OpenLibraryDoc {
  key: string;
  title: string;
  author_name?: string[];
  first_publish_year?: number;
  isbn?: string[];
  cover_i?: number;
  subject?: string[];
}

const SEARCH_URL = "https://openlibrary.org/search.json";
const COVER_BASE = "https://covers.openlibrary.org/b";

export async function searchBooks(
  query: string,
): Promise<OpenLibraryResult[]> {
  const params = new URLSearchParams({
    q: query,
    limit: "5",
    fields: "key,title,author_name,first_publish_year,isbn,cover_i,subject",
  });

  const response = await fetch(`${SEARCH_URL}?${params}`);
  if (!response.ok) {
    throw new Error(`Open Library API error: ${response.statusText}`);
  }

  const data = (await response.json()) as { docs: OpenLibraryDoc[] };
  return (data.docs ?? []).map(mapDoc);
}

export async function searchByIsbn(
  isbn: string,
): Promise<OpenLibraryResult[]> {
  const cleaned = isbn.replace(/[^0-9X]/gi, "");
  if (!cleaned) return [];

  const params = new URLSearchParams({
    isbn: cleaned,
    limit: "5",
    fields: "key,title,author_name,first_publish_year,isbn,cover_i,subject",
  });

  const response = await fetch(`${SEARCH_URL}?${params}`);
  if (!response.ok) {
    throw new Error(`Open Library API error: ${response.statusText}`);
  }

  const data = (await response.json()) as { docs: OpenLibraryDoc[] };
  return (data.docs ?? []).map(mapDoc);
}

function mapDoc(doc: OpenLibraryDoc): OpenLibraryResult {
  const isbn = selectBestIsbn(doc.isbn);
  const parsed = parseSeries(doc.subject);

  return {
    title: doc.title,
    author: doc.author_name?.[0],
    firstPublishYear:
      doc.first_publish_year != null
        ? String(doc.first_publish_year)
        : undefined,
    isbn,
    coverUrl: buildCoverUrl(doc.cover_i, isbn),
    series: parsed?.series,
    seriesNumber: parsed?.seriesNumber,
  };
}

function selectBestIsbn(
  isbns: string[] | undefined,
): string | undefined {
  if (!isbns?.length) return undefined;
  const cleaned = isbns.map((s) => s.replace(/[^0-9X]/gi, ""));
  return (
    cleaned.find((s) => s.length === 13) ??
    cleaned.find((s) => s.length === 10) ??
    undefined
  );
}

function buildCoverUrl(
  coverId: number | undefined,
  isbn: string | undefined,
): string | undefined {
  if (coverId) return `${COVER_BASE}/id/${coverId}-M.jpg`;
  if (isbn) return `${COVER_BASE}/isbn/${isbn}-M.jpg`;
  return undefined;
}

/**
 * Attempt to find a cover image for a book.
 *
 * Strategy (fastest first):
 * 1. If the book has an ISBN, check the covers API directly with a HEAD
 *    request — this is near-instant compared to a search query.
 * 2. Fall back to a title+author search (slower, but works without ISBN).
 */
export async function findCover(
  title: string,
  author: string,
  isbn?: string,
): Promise<string | undefined> {
  // Fast path: check the covers API directly by ISBN
  if (isbn) {
    const cleaned = isbn.replace(/[^0-9X]/gi, "");
    if (cleaned) {
      try {
        const url = `${COVER_BASE}/isbn/${cleaned}-M.jpg?default=false`;
        const resp = await fetch(url, { method: "HEAD" });
        if (resp.ok) return `${COVER_BASE}/isbn/${cleaned}-M.jpg`;
      } catch {
        // Fall through to search
      }
    }
  }

  // Slow path: search by title + author
  try {
    const results = await searchBooks(`${title} ${author}`);
    const hit = results.find((r) => r.coverUrl);
    if (hit) return hit.coverUrl;
  } catch {
    // No cover found
  }

  return undefined;
}

const SERIES_RE =
  /^(.+?)[\s,]*(?:#|book\s*|vol(?:ume)?\.?\s*)(\d+[\w.]*)$/i;

function parseSeries(
  subjects: string[] | undefined,
): { series: string; seriesNumber: string } | undefined {
  if (!subjects?.length) return undefined;
  for (const subj of subjects) {
    const m = SERIES_RE.exec(subj);
    if (m) return { series: m[1].trim(), seriesNumber: m[2] };
  }
  return undefined;
}
